import { createPortal } from "react-dom";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";

const EASE = [0.16, 1, 0.3, 1];

// overlays must escape transformed/perspective ancestors (page-flip wrapper)
export const Portal = ({ children }) => createPortal(children, document.body);

export const MaskLine = ({ children, delay = 0, className = "" }) => (
    <span className={`block overflow-hidden ${className}`}>
        <motion.span
            className="block will-change-transform"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

export const FadeUp = ({
    children,
    delay = 0,
    className = "",
    y = 30,
    once = true,
}) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once, amount: 0.15 }}
        transition={{ duration: 0.9, delay, ease: EASE }}
    >
        {children}
    </motion.div>
);

export const SectionLabel = ({ num, en, fr, light = false }) => {
    const { lang } = useLang();
    return (
        <FadeUp>
            <div
                className={`flex items-center gap-4 font-mono text-[11px] tracking-[0.35em] ${
                    light ? "text-ink/60" : "text-cream/60"
                }`}
            >
                <span className="inline-block h-2 w-2 bg-violet" />
                {tx(lang, en && fr ? { en, fr } : null) || (lang === "fr" ? fr : en)}
            </div>
        </FadeUp>
    );
};

export const EASE_OUT = EASE;

// scroll-linked parallax drift — keeps the page alive while scrolling
export const Parallax = ({ children, className = "", from = 40, to = -40 }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [from, to]);
    return (
        <motion.div ref={ref} style={{ y }} className={className}>
            {children}
        </motion.div>
    );
};

// horizontal drift for big titles as you scroll
export const DriftTitle = ({ children, className = "", dir = 1 }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const x = useTransform(scrollYProgress, [0, 1], [`${3 * dir}%`, `${-3 * dir}%`]);
    return (
        <motion.div ref={ref} style={{ x }} className={className}>
            {children}
        </motion.div>
    );
};

// image reveal: slow zoom-out + unblur when entering the viewport
export const ScaleIn = ({ children, className = "", delay = 0 }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, scale: 1.08, filter: "blur(10px)" }}
        whileInView={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
    >
        {children}
    </motion.div>
);

// slide-in from the side — good for list rows / services
export const SlideIn = ({ children, className = "", delay = 0, from = -40 }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, x: from }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.9, delay, ease: EASE }}
    >
        {children}
    </motion.div>
);

export const BlurBackdrop = ({ src, tint = "bg-cream/75" }) => (
    <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
    >
        <img
            src={src}
            alt=""
            loading="lazy"
            className="h-full w-full scale-150 object-cover opacity-55 blur-[40px]"
        />
        <div className={`absolute inset-0 ${tint}`} />
        <div className="absolute inset-0 bg-gradient-to-br from-violet/[0.10] via-transparent to-gold/[0.08]" />
    </div>
);
